import React, { useEffect, useState } from "react";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearIcon from "@mui/icons-material/Clear";
import CancelIcon from "@mui/icons-material/Cancel";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import baseUrl from "../../../../api/baseUrl";
import { toast } from "react-toastify";

function ReScheduleTray({ data, setSelectReSchedule, onCancel, selectedId }: any) {
  const [select, setselect] = useState("");

  useEffect(() => {
    setselect(selectedId ?? "");
  }, [selectedId]);

  const handleSelect = (e: any) => {
    setSelectReSchedule(e);
    setselect(e);
    if (e) {
      toast.info("Now click an empty time on the calendar below.", { autoClose: 5000 });
    }
  };

  const handleCancelAppointment = async (e: React.MouseEvent, appointmentId: string) => {
    e.stopPropagation();

    try {
      const response = await baseUrl.patch(
        "/appointment/statusAppointmentById",
        {},
        {
          headers: {
            appointmentid: appointmentId,
            status: "canceled",
          },
        }
      );

      if (response.data?.success) {
        toast.success("Appointment canceled successfully!");
        if (select === appointmentId) {
          setSelectReSchedule("");
          setselect("");
        }
        if (onCancel) {
          onCancel();
        }
      } else {
        toast.error("Failed to cancel appointment");
      }
    } catch (error: any) {
      console.error("Error canceling appointment:", error);
      toast.error(error?.response?.data?.message || "Failed to cancel appointment");
    }
  };

  const selectedPatient = data?.find((item: any) => item?._id === select);

  return (
    <div className="border-b border-slate-200">
      <div className="bg-amber-500 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <EventRepeatIcon className="text-white" sx={{ fontSize: 18 }} />
          <h5 className="text-white font-semibold text-sm uppercase tracking-wide">
            Reschedule tray
          </h5>
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">
            {data?.length ?? 0} waiting
          </span>
        </div>
      </div>

      <div className="px-4 py-3 bg-amber-50/80 border-b border-amber-100">
        <ol className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs font-medium text-amber-900">
          <li className={`flex items-center gap-1.5 ${select ? "text-amber-700" : "text-amber-900"}`}>
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                select ? "bg-amber-500 text-white" : "bg-amber-200 text-amber-800"
              }`}
            >
              1
            </span>
            Select patient
          </li>
          <li
            className={`flex items-center gap-1.5 ${
              select ? "text-amber-900" : "text-amber-600/80"
            }`}
          >
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                select ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-700"
              }`}
            >
              2
            </span>
            <CalendarMonthIcon sx={{ fontSize: 14 }} />
            Click empty calendar slot
          </li>
          <li className="flex items-center gap-1.5 text-amber-600/80">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700">
              3
            </span>
            <TouchAppIcon sx={{ fontSize: 14 }} />
            Confirm in popup
          </li>
        </ol>
        {select && selectedPatient ? (
          <p className="mt-2 text-sm text-amber-800">
            Rescheduling{" "}
            <span className="font-semibold">{selectedPatient?.patientId?.fullName}</span>
            — pick a new time on the calendar.
          </p>
        ) : (
          <p className="mt-2 text-sm text-amber-700/90">
            Choose who to reschedule first. New appointments are paused until the tray is
            empty.
          </p>
        )}
      </div>

      <div className="p-3 pb-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
          {data?.map((item: any, i: number) => (
            <div
              key={i}
              onClick={() => handleSelect(item?._id)}
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all ${
                select === item?._id
                  ? "bg-amber-100 border-2 border-amber-400 shadow-sm"
                  : "bg-slate-50 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                    select === item?._id
                      ? "bg-amber-500 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {item?.patientId?.fullName?.charAt(0)?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {item?.patientId?.fullName}
                  </p>
                  <p className="text-xs text-slate-500">{item?.patientId?.regNo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {select === item?._id && (
                  <CheckCircleIcon className="text-amber-500" sx={{ fontSize: 18 }} />
                )}
                <button
                  type="button"
                  onClick={(e) => handleCancelAppointment(e, item?._id)}
                  className="w-6 h-6 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                  title="Cancel appointment"
                >
                  <CancelIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {select && (
          <div className="pt-2 mt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={() => handleSelect("")}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
            >
              <ClearIcon sx={{ fontSize: 16 }} />
              Clear selection
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReScheduleTray;
